import { useId, useState, type FormEvent, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Erro } from '../componentes/Estados.js';
import { ExplicacaoTabelas } from '../componentes/ExplicacaoTabelas.js';
import { ROTULO_PAGAMENTO, rotuloTabela } from '../lib/formato.js';
import { mensagemDoErro, requisitar } from '../lib/api.js';
import {
  baseDoModo,
  buscarNiveisDelCredere,
  rotuloDoModo,
  segmentoDoModo,
  TABELA_DEL_CREDERE,
  type ModoGerador,
} from '../estado/modo.js';
import { segmentoPorCode } from '@orc/shared';
import './Orcamento.css';

interface Cliente {
  id: string;
  nomeRazao: string;
  documento: string | null;
}

/**
 * Criação do rascunho — Story 8.3 (RF-21).
 *
 * Cliente, tabela e forma de pagamento são escolhidos **antes** dos itens
 * porque os três entram no cálculo de cada linha: a tabela decide o preço-base
 * e o pagamento aplica o fator de cartão na base (RF-19). Trocá-los depois
 * mudaria todos os itens de uma vez.
 */
export function OrcamentoNovo({ modo = 'comum' }: { modo?: ModoGerador }): JSX.Element {
  const navegar = useNavigate();
  const delCredere = modo === 'del_credere';
  const base = baseDoModo(modo);

  const [clientId, setClientId] = useState('');
  /*
   * A tabela inicial sai da SEÇÃO — S5, revista na S12.
   *
   * Todas as seções nascem em RT 5 / RT 10 (o quadrante RT10), que é a venda de
   * revenda com o RT já embutido pela Villagres. Vinílicos e Rodapé Engenharia
   * nasciam em CPF, e foi assim que o ORC-2026-0053 saiu em Engenharia com RT
   * zerado, R$ 85 abaixo do pedido Villagres. Continua trocável: Engenharia é
   * uma tabela que existe, só deixou de ser o ponto de partida.
   */
  const [tabela, setTabela] = useState(
    delCredere ? TABELA_DEL_CREDERE : (segmentoPorCode(modo)?.tabelaPadrao ?? 'RT10'),
  );
  /*
   * Nível padrão do orçamento — obrigatório no del credere.
   *
   * O servidor RECUSA criar sem ele, e por um bom motivo: a base do preço é
   * a coluna escolhida, então sem nível não há base. O default `del_5` é o
   * meio da faixa e o mesmo que o Villa Arte já usa; o vendedor troca aqui
   * ou item a item depois.
   */
  const [delNivelPadrao, setDelNivelPadrao] = useState('del_5');
  /**
   * Comissão padrão do pedido — S4, só nas tabelas SEM del credere.
   *
   * Vazio de propósito, e é o ponto: ausência significa "a regra de sempre"
   * (`pricing_rule`, hoje comissão 5%). Nascer preenchido faria todo orçamento
   * novo gravar um nível que ninguém escolheu, e a trilha de auditoria passaria
   * a registrar uma decisão comercial que não houve.
   *
   * É o que mantém o golden verde: quem não mexe aqui tem o comportamento
   * anterior à S4, byte a byte.
   */
  const [comissaoPadrao, setComissaoPadrao] = useState('');
  const [paymentType, setPaymentType] = useState<'avista' | 'cartao'>('avista');
  const [novoCliente, setNovoCliente] = useState('');
  const [documento, setDocumento] = useState('');
  const [criandoCliente, setCriandoCliente] = useState(false);
  const [erro, setErro] = useState<unknown>(undefined);
  const [enviando, setEnviando] = useState(false);
  const idExplicacao = useId();

  /*
   * Os seis níveis vêm do SERVIDOR. Escrevê-los aqui duplicaria o
   * pareamento coluna × condição comercial, que tem uma definição só.
   */
  const niveis = useQuery({
    queryKey: ['niveis-del-credere'],
    // Envolvido: o TanStack passa o CONTEXTO como 1o argumento, e desde a S4
    // a funcao aceita a tabela ali — sem o wrapper ela receberia o contexto.
    queryFn: async () => buscarNiveisDelCredere(),
    enabled: delCredere,
  });

  /*
   * As seis comissões da tabela escolhida — S4.
   *
   * A `tabela` entra na chave do cache: RT10 e CPF podem ter escalas
   * diferentes no futuro (a RT5 já está no horizonte), e um cache compartilhado
   * mostraria a lista de uma tabela embaixo do nome da outra.
   */
  const comissoes = useQuery({
    queryKey: ['niveis-comissao', tabela],
    queryFn: async () => buscarNiveisDelCredere(tabela),
    enabled: !delCredere,
  });

  const clientes = useQuery({
    queryKey: ['clientes'],
    queryFn: async () => (await requisitar<Cliente[]>('/api/clientes')).dados,
  });

  async function aoEnviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setErro(undefined);
    setEnviando(true);
    try {
      let alvo = clientId;

      if (criandoCliente) {
        const c = await requisitar<Cliente>('/api/clientes', {
          metodo: 'POST',
          corpo: {
            nomeRazao: novoCliente.trim(),
            documento: documento.trim() === '' ? null : documento.trim(),
          },
        });
        alvo = c.dados.id;
      }

      const r = await requisitar<{ id: string }>('/api/orcamentos', {
        metodo: 'POST',
        corpo: {
          clientId: alvo,
          tabela,
          paymentType,
          /*
           * No del credere o nível é obrigatório; nas demais, a comissão só vai
           * quando o vendedor escolheu uma. Mandar o campo vazio gravaria uma
           * decisão que não houve — e é a ausência que preserva a regra.
           */
          ...(delCredere
            ? { delNivelPadrao }
            : comissaoPadrao === ''
              ? {}
              : { delNivelPadrao: comissaoPadrao }),
          /*
           * A SEÇÃO de onde o pedido veio — S5. É o que faz a lista daquela
           * tela encontra-lo depois; sem isto ele nasceria na seção comum.
           */
          ...(segmentoDoModo(modo) === null ? {} : { segmento: segmentoDoModo(modo) }),
        },
      });
      navegar(`${base}/${r.dados.id}`);
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  }

  const temCliente = criandoCliente ? novoCliente.trim() !== '' : clientId !== '';
  // No del credere, criar sem nível é recusado pelo servidor — a tela não
  // deixa nem tentar enquanto a lista não chegou.
  const podeEnviar = temCliente && (!delCredere || (niveis.data ?? []).length > 0);

  return (
    <section className="pagina pagina--estreita">
      <header className="pagina__cabecalho">
        <div>
          <h1>{modo === 'comum' ? 'Novo orçamento' : `Novo orçamento ${rotuloDoModo(modo)}`}</h1>
          <p className="pagina__sub">
            {delCredere
              ? 'A coluna escolhida vira o padrão do orçamento — cada item pode usar outra.'
              : 'Tabela e forma de pagamento entram no cálculo de todos os itens.'}
          </p>
        </div>
      </header>

      <form
        className="cartao formulario"
        onSubmit={(e) => {
          void aoEnviar(e);
        }}
      >
        <div className="formulario__alternar">
          <button
            type="button"
            className={criandoCliente ? 'aba' : 'aba aba--ativa'}
            onClick={() => {
              setCriandoCliente(false);
            }}
          >
            Cliente existente
          </button>
          <button
            type="button"
            className={criandoCliente ? 'aba aba--ativa' : 'aba'}
            onClick={() => {
              setCriandoCliente(true);
            }}
          >
            Novo cliente
          </button>
        </div>

        {criandoCliente ? (
          <>
            <label className="campo">
              <span className="campo__rotulo">Nome / razão social</span>
              <input
                className="campo__entrada"
                value={novoCliente}
                onChange={(e) => {
                  setNovoCliente(e.target.value);
                }}
                required
              />
            </label>
            <label className="campo">
              <span className="campo__rotulo">CNPJ / CPF (opcional)</span>
              <input
                className="campo__entrada"
                value={documento}
                onChange={(e) => {
                  setDocumento(e.target.value);
                }}
              />
            </label>
          </>
        ) : clientes.isError ? (
          <Erro
            erro={clientes.error}
            aoRepetir={() => {
              void clientes.refetch();
            }}
          />
        ) : (
          <label className="campo">
            <span className="campo__rotulo">Cliente</span>
            <select
              className="campo__entrada"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
              }}
              required
            >
              <option value="">Selecione…</option>
              {(clientes.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nomeRazao}
                  {c.documento === null ? '' : ` · ${c.documento}`}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="formulario__linha">
          {delCredere ? (
            /*
             * Sem seletor de tabela aqui: nesta seção o quadrante É a seção.
             * Oferecer RT10/CPF numa tela chamada "Del Credere" seria
             * exatamente a mistura que o cliente pediu para não existir.
             */
            <label className="campo">
              {/*
                O rótulo diz que a lista está ordenada — sem isso, "a mais
                barata" na primeira opção parece um adjetivo solto em vez da
                informação de que as seis estão em escala.
              */}
              <span className="campo__rotulo">
                Coluna padrão (comissão · del credere) — da mais barata à mais cara
              </span>
              <select
                className="campo__entrada"
                value={delNivelPadrao}
                aria-label="Coluna padrão do orçamento"
                onChange={(e) => {
                  setDelNivelPadrao(e.target.value);
                }}
              >
                {/*
                  Sem PREÇO aqui, e não por esquecimento: esta tela escolhe a
                  coluna padrão ANTES de existir item, então não há produto e
                  não há preço. O seletor por item, que tem produto, mostra o
                  valor de cada coluna.

                  O que dá para dizer sem produto é a posição na escala — e é
                  o que desfaz a confusão que o cliente relatou: ele deixa de
                  precisar decorar qual comissão é a barata.
                */}
                {(niveis.data ?? []).map((n) => (
                  <option key={n.nivel} value={n.nivel}>
                    {n.rotulo}
                    {n.posicaoPreco === 'mais_barata' && ' — a mais barata'}
                    {n.posicaoPreco === 'mais_cara' && ' — a mais cara'}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <>
              <label className="campo">
                <span className="campo__rotulo">Tabela de preço</span>
                <select
                  className="campo__entrada"
                  value={tabela}
                  aria-describedby={idExplicacao}
                  onChange={(e) => {
                    setTabela(e.target.value);
                  }}
                >
                  <option value="RT10">{rotuloTabela('RT10')}</option>
                  <option value="CPF">{rotuloTabela('CPF')}</option>
                </select>
              </label>

              {/*
                A comissão padrão do pedido — S4.

                Aqui é `<select>`, e não o campo digitável do item, por um
                motivo concreto: esta tela escolhe ANTES de existir item, então
                não há produto e **não há preço para comparar**. O que faz o
                campo digitável valer a pena na linha é justamente mostrar
                quanto cada comissão custa; sem preço ele seria só um jeito mais
                difícil de escolher entre seis.

                A primeira opção é VAZIA e é a que vem marcada: sem escolha, a
                `pricing_rule` responde como sempre respondeu.
              */}
              <label className="campo">
                <span className="campo__rotulo">Comissão padrão (opcional)</span>
                <select
                  className="campo__entrada"
                  value={comissaoPadrao}
                  aria-label="Comissão padrão do orçamento"
                  onChange={(e) => {
                    setComissaoPadrao(e.target.value);
                  }}
                >
                  <option value="">Padrão da tabela (5%)</option>
                  {(comissoes.data ?? []).map((n) => (
                    <option key={n.nivel} value={n.nivel}>
                      {n.rotulo}
                      {n.posicaoPreco === 'mais_barata' && ' — a mais barata'}
                      {n.posicaoPreco === 'mais_cara' && ' — a mais cara'}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          <label className="campo">
            <span className="campo__rotulo">Forma de pagamento</span>
            <select
              className="campo__entrada"
              value={paymentType}
              onChange={(e) => {
                setPaymentType(e.target.value === 'cartao' ? 'cartao' : 'avista');
              }}
            >
              {/* O `value` continua `cartao` — o que muda é só a palavra. */}
              <option value="avista">{ROTULO_PAGAMENTO['avista']}</option>
              <option value="cartao">{ROTULO_PAGAMENTO['cartao']}</option>
            </select>
          </label>
        </div>

        {/* Fora da grade de campos: é uma nota sobre o seletor de tabela, não um
            campo a mais — dentro dela ocuparia uma coluna e quebraria a linha. */}
        {!delCredere && <ExplicacaoTabelas id={idExplicacao} tabelas={['RT10', 'CPF']} />}

        {erro !== undefined && (
          <p className="aviso" role="alert">
            {mensagemDoErro(erro)}
          </p>
        )}

        <div className="formulario__acoes">
          <button
            type="button"
            className="botao botao--neutro"
            onClick={() => {
              navegar(base);
            }}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="botao botao--primario"
            disabled={enviando || !podeEnviar}
          >
            {enviando ? 'Criando…' : 'Criar rascunho'}
          </button>
        </div>
      </form>
    </section>
  );
}
